import React, { useState, useEffect, useMemo } from "react";
import { Container, Card, CardBody, Spinner } from "reactstrap";
import { DashboardPageHeader, TableSkeleton } from "../../Components/Common/DashboardKit";
import api from "../../api";

const WhosInBuilding = () => {
    document.title = "Who's In The Building | SSW Dashboard";

    const [activeTab, setActiveTab] = useState("salaried");
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 10;

    // ── Fetch ──────────────────────────────────────────────
    const fetchEmployees = async (type) => {
        setLoading(true);
        setSearch("");
        setPage(1);
        try {
            console.log(`Fetching employees for type: ${type}`);
            const res = await api.get(
                `WhosInBuilding/${type}`
            );
            setEmployees(Array.isArray(res.data) ? res.data : []);
            console.log(`Fetched employees for type ${type}:`, res.data);
        } catch (err) {
            console.error("Error fetching employees:", err);
            setEmployees([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchEmployees(activeTab); }, [activeTab]);

    // ── Filter + paginate ──────────────────────────────────
    const filtered = useMemo(() =>
        employees.filter(e =>
            e.name?.toLowerCase().includes(search.toLowerCase()) ||
            e.empId?.toString().includes(search)
        ),
        [employees, search]
    );

    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const paginated = filtered.slice(
        (page - 1) * PAGE_SIZE,
        page * PAGE_SIZE
    );

    // ── Summary counts ─────────────────────────────────────
    const inCount = employees.filter(e =>
        String(e.status || "").toUpperCase() === "IN"
    ).length;
    const outCount = employees.filter(e =>
        String(e.status || "").toUpperCase() === "OUT"
    ).length;

    // ── Styles ─────────────────────────────────────────────
    const btnBase = {
        padding: "5px 10px",
        borderRadius: "6px",
        border: "1px solid var(--color-border-tertiary)",
        background: "var(--color-background-primary)",
        color: "var(--color-text-primary)",
        cursor: "pointer",
        fontSize: "12px"
    };
    const btnDisabled = {
        ...btnBase,
        color: "var(--color-text-secondary)",
        cursor: "not-allowed"
    };
    const btnActive = {
        ...btnBase,
        background: "#534AB7",
        color: "#fff",
        fontWeight: 600,
        border: "1px solid #534AB7"
    };

    // ── Page numbers ───────────────────────────────────────
    const pageNumbers = Array.from(
        { length: totalPages }, (_, i) => i + 1
    )
        .filter(p =>
            p === 1 || p === totalPages || Math.abs(p - page) <= 1
        )
        .reduce((acc, p, i, arr) => {
            if (i > 0 && p - arr[i - 1] > 1) acc.push("...");
            acc.push(p);
            return acc;
        }, []);

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-building-4-line"
                    eyebrow="Attendance"
                    title="Who's In The Building?"
                    subtitle="Live view of employees currently clocked in."
                    tone="blue"
                    breadcrumb={[
                        { label: "Dashboards", to: "/" },
                        { label: "Attendance", to: "/workflows?wf=pac" },
                        { label: "Who's In The Building" },
                    ]}
                />

                {/* ── Summary cards ──────────────────────── */}
                <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                    gap: "12px",
                    marginBottom: "20px"
                }}>
                    {[
                        {
                            label: "Total Employees",
                            value: employees.length,
                            color: "#534AB7",
                            icon: "ri-team-line"
                        },
                        {
                            label: "In Office",
                            value: inCount,
                            color: "#198754",
                            icon: "ri-user-follow-line"
                        },
                        {
                            label: "Out",
                            value: outCount,
                            color: "#dc3545",
                            icon: "ri-user-unfollow-line"
                        },
                    ].map(({ label, value, color, icon }) => (
                        <div key={label} style={{
                            background: "var(--color-background-primary)",
                            border: "0.5px solid var(--color-border-tertiary)",
                            borderRadius: "10px",
                            padding: "14px 16px",
                            borderTop: `3px solid ${color}`
                        }}>
                            <div style={{
                                fontSize: "12px",
                                color: "var(--color-text-secondary)",
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                                marginBottom: "6px"
                            }}>
                                <i className={icon} style={{ color }} />
                                {label}
                            </div>
                            <div style={{
                                fontSize: "24px",
                                fontWeight: 600,
                                color
                            }}>
                                {loading ? <Spinner size="sm" /> : value}
                            </div>
                        </div>
                    ))}
                </div>

                {/* ── Main card ──────────────────────────── */}
                <Card>
                    <CardBody>

                        {/* ── Tabs + Search ── */}
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "16px",
                            flexWrap: "wrap",
                            gap: "12px"
                        }}>
                            {/* Tabs */}
                            <div style={{
                                display: "flex",
                                border: "1px solid var(--color-border-tertiary)",
                                borderRadius: "8px",
                                overflow: "hidden"
                            }}>
                                {["salaried", "hourly"].map((tab) => (
                                    <button
                                        key={tab}
                                        onClick={() => setActiveTab(tab)}
                                        style={{
                                            padding: "7px 20px",
                                            border: "none",
                                            fontSize: "13px",
                                            fontWeight: activeTab === tab ? 600 : 400,
                                            background: activeTab === tab
                                                ? "#534AB7"
                                                : "var(--color-background-primary)",
                                            color: activeTab === tab
                                                ? "#fff"
                                                : "var(--color-text-secondary)",
                                            cursor: "pointer",
                                            textTransform: "capitalize"
                                        }}
                                    >
                                        {tab === "salaried"
                                            ? <><i className="ri-briefcase-line me-1" />Salaried</>
                                            : <><i className="ri-time-line me-1" />Hourly</>
                                        }
                                    </button>
                                ))}
                            </div>

                            {/* Search + Refresh */}
                            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                <div style={{ position: "relative" }}>
                                    <i className="ri-search-line"
                                        style={{
                                            position: "absolute",
                                            left: "10px",
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            color: "var(--color-text-secondary)",
                                            fontSize: "14px"
                                        }}
                                    />
                                    <input
                                        type="text"
                                        placeholder="Search name or ID..."
                                        value={search}
                                        onChange={(e) => {
                                            setSearch(e.target.value);
                                            setPage(1);
                                        }}
                                        style={{
                                            paddingLeft: "32px",
                                            paddingRight: "12px",
                                            paddingTop: "6px",
                                            paddingBottom: "6px",
                                            borderRadius: "8px",
                                            border: "1px solid var(--color-border-tertiary)",
                                            background: "var(--color-background-primary)",
                                            color: "var(--color-text-primary)",
                                            fontSize: "13px",
                                            width: "220px"
                                        }}
                                    />
                                </div>

                                <button
                                    onClick={() => fetchEmployees(activeTab)}
                                    style={{
                                        padding: "6px 14px",
                                        borderRadius: "8px",
                                        border: "1px solid var(--color-border-tertiary)",
                                        background: "var(--color-background-primary)",
                                        color: "var(--color-text-secondary)",
                                        cursor: "pointer",
                                        fontSize: "13px",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "5px"
                                    }}
                                    title="Refresh"
                                >
                                    <i className="ri-refresh-line" />
                                    Refresh
                                </button>
                            </div>
                        </div>

                        {/* ── Table ── */}
                        {loading ? (
                            <div className="table-responsive">
                                <table className="table align-middle mb-0">
                                    <tbody><TableSkeleton rows={8} columns={4} /></tbody>
                                </table>
                            </div>
                        ) : filtered.length === 0 ? (
                            <div className="text-center py-5 text-muted">
                                <i className="ri-user-search-line fs-1 d-block mb-3" />
                                {search
                                    ? `No results for "${search}"`
                                    : "No employees found."
                                }
                            </div>
                        ) : (
                            <>
                                <table style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                    fontSize: "13px"
                                }}>
                                    <thead>
                                        <tr style={{
                                            background: "var(--color-background-secondary)",
                                            borderBottom: "1px solid var(--color-border-tertiary)"
                                        }}>
                                            {["#", "Emp ID", "Name", "Status", "Returning At"].map(h => (
                                                <th key={h} style={{
                                                    padding: "10px 14px",
                                                    fontWeight: 600,
                                                    fontSize: "12px",
                                                    color: "var(--color-text-secondary)",
                                                    textAlign: h === "#" ? "center" : "left",
                                                    width: h === "#" ? "50px" : "auto"
                                                }}>
                                                    {h}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginated.map((emp, idx) => {
                                            const statusUp = String(emp.status || "").toUpperCase();
                                            const isIn = statusUp === "IN";
                                            const rowNum = (page - 1) * PAGE_SIZE + idx + 1;

                                            return (
                                                <tr key={emp.empId}
                                                    style={{
                                                        borderBottom: "0.5px solid var(--color-border-tertiary)",
                                                        background: idx % 2 === 0
                                                            ? "var(--color-background-primary)"
                                                            : "var(--color-background-secondary)"
                                                    }}
                                                >
                                                    {/* # */}
                                                    <td style={{
                                                        padding: "10px 14px",
                                                        textAlign: "center",
                                                        color: "var(--color-text-secondary)",
                                                        fontSize: "12px"
                                                    }}>
                                                        {rowNum}
                                                    </td>

                                                    {/* Emp ID */}
                                                    <td style={{ padding: "10px 14px" }}>
                                                        <span style={{
                                                            fontFamily: "monospace",
                                                            fontSize: "12px",
                                                            padding: "2px 8px",
                                                            borderRadius: "4px",
                                                            background: "var(--color-background-secondary)",
                                                            color: "var(--color-text-secondary)",
                                                            border: "0.5px solid var(--color-border-tertiary)"
                                                        }}>
                                                            {emp.empId}
                                                        </span>
                                                    </td>

                                                    {/* Name */}
                                                    <td style={{ padding: "10px 14px" }}>
                                                        <div style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: "10px"
                                                        }}>
                                                            {/* Avatar initials */}
                                                            <div style={{
                                                                width: "32px",
                                                                height: "32px",
                                                                borderRadius: "50%",
                                                                background: isIn
                                                                    ? "#19875420"
                                                                    : "#dc354520",
                                                                color: isIn
                                                                    ? "#198754"
                                                                    : "#dc3545",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                fontSize: "11px",
                                                                fontWeight: 600,
                                                                flexShrink: 0
                                                            }}>
                                                                {emp.name
                                                                    ?.split(" ")
                                                                    .map(n => n[0])
                                                                    .slice(0, 2)
                                                                    .join("")
                                                                    .toUpperCase()
                                                                }
                                                            </div>
                                                            <span style={{
                                                                fontWeight: 500,
                                                                color: "var(--color-text-primary)"
                                                            }}>
                                                                {emp.name}
                                                            </span>
                                                        </div>
                                                    </td>

                                                    {/* Status */}
                                                    <td style={{ padding: "10px 14px" }}>
                                                        <span style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            gap: "5px",
                                                            padding: "4px 12px",
                                                            borderRadius: "20px",
                                                            fontSize: "12px",
                                                            fontWeight: 600,
                                                            background: isIn
                                                                ? "#19875420"
                                                                : "#dc354520",
                                                            color: isIn
                                                                ? "#198754"
                                                                : "#dc3545",
                                                        }}>
                                                            <span style={{
                                                                width: "6px",
                                                                height: "6px",
                                                                borderRadius: "50%",
                                                                background: isIn
                                                                    ? "#198754"
                                                                    : "#dc3545",
                                                                flexShrink: 0
                                                            }} />
                                                            {emp.status || "-"}
                                                        </span>
                                                    </td>

                                                    {/* Returning At */}
                                                    <td style={{
                                                        padding: "10px 14px",
                                                        color: "var(--color-text-secondary)",
                                                        fontSize: "12px"
                                                    }}>
                                                        {emp.returningAt || "N/A"}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                {/* ── Pagination ── */}
                                {totalPages > 1 && (
                                    <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        marginTop: "16px",
                                        paddingTop: "12px",
                                        borderTop: "0.5px solid var(--color-border-tertiary)"
                                    }}>
                                        <span style={{
                                            fontSize: "13px",
                                            color: "var(--color-text-secondary)"
                                        }}>
                                            Showing{" "}
                                            {(page - 1) * PAGE_SIZE + 1}–
                                            {Math.min(page * PAGE_SIZE, filtered.length)}
                                            {" "}of {filtered.length} employees
                                        </span>

                                        <div style={{ display: "flex", gap: "4px" }}>
                                            <button
                                                onClick={() => setPage(1)}
                                                disabled={page === 1}
                                                style={page === 1 ? btnDisabled : btnBase}
                                            >
                                                <i className="ri-skip-back-line" />
                                            </button>

                                            <button
                                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                                disabled={page === 1}
                                                style={page === 1 ? btnDisabled : btnBase}
                                            >
                                                <i className="ri-arrow-left-s-line" />
                                                Prev
                                            </button>

                                            {pageNumbers.map((p, i) =>
                                                p === "..." ? (
                                                    <span key={`dots-${i}`} style={{
                                                        padding: "5px 6px",
                                                        fontSize: "12px",
                                                        color: "var(--color-text-secondary)"
                                                    }}>…</span>
                                                ) : (
                                                    <button
                                                        key={p}
                                                        onClick={() => setPage(p)}
                                                        style={page === p ? btnActive : btnBase}
                                                    >
                                                        {p}
                                                    </button>
                                                )
                                            )}

                                            <button
                                                onClick={() => setPage(p =>
                                                    Math.min(totalPages, p + 1)
                                                )}
                                                disabled={page === totalPages}
                                                style={page === totalPages
                                                    ? btnDisabled : btnBase}
                                            >
                                                Next
                                                <i className="ri-arrow-right-s-line ms-1" />
                                            </button>

                                            <button
                                                onClick={() => setPage(totalPages)}
                                                disabled={page === totalPages}
                                                style={page === totalPages
                                                    ? btnDisabled : btnBase}
                                            >
                                                <i className="ri-skip-forward-line" />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default WhosInBuilding;